import { useCallback } from "react";
import { TabButton, TabButtonVariant } from "@ui-kit/TabButton/TabButton";
import { useTabSingleSelect } from "./useTabSelect";
import { TabValue, TabGroup } from "./Tab";

interface TabSingleSelectProps<T extends TabValue> {
  /**
   * Array of tab objects containing name and label
   */
  tabs: TabGroup<T>;
  /**
   * Currently active tab name
   */
  activeTab?: T | null;
  /**
   * Callback when a tab is selected
   */
  onTabSelect?: (tabValue: T) => void;
  /**
   * Size of the tab buttons
   */
  size?: "default" | "large" | "small" | "small-default";
  /**
   * If the tab buttons should be disabled
   */
  disabled?: boolean;
  /**
   * Let horizontal padding flex.
   */
  flex?: boolean;
  /**
   * Variant of the tab buttons
   */
  variant?: TabButtonVariant;
  /**
   * Test ID for testing purposes
   */
  testID: string;
}

const TabSingleSelect = <T extends TabValue>({
  tabs,
  activeTab: controlledActiveTab = null,
  onTabSelect: controlledOnTabSelect,
  size = "default",
  disabled = false,
  variant = "default",
  testID,
  flex = false,
}: TabSingleSelectProps<T>) => {
  const initialProps =
    controlledActiveTab === null
      ? {
          onTabSelect: controlledOnTabSelect,
        }
      : {
          initialTab: controlledActiveTab,
          onTabSelect: controlledOnTabSelect,
        };
  const { activeTab, onTabSelect } = useTabSingleSelect<T>(initialProps);

  const createTabHandler = useCallback(
    (tabValue: T) => {
      return () => onTabSelect(tabValue);
    },
    [onTabSelect],
  );

  return (
    <div
      data-testid={testID}
      style={{
        display: "flex",
        gap: "8px",
        ...(flex && { flex: 1 }),
      }}>
      {tabs.map((tab) => (
        <TabButton
          key={tab.value.toString()}
          label={tab.label}
          active={activeTab === tab.value}
          onPress={createTabHandler(tab.value)}
          size={size}
          disabled={disabled}
          variant={variant}
          testID={`${testID}-${tab.value.toString()}`}
          flex={flex}
        />
      ))}
    </div>
  );
};

export { TabSingleSelect };
